import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import type { BvBill, BvBillFormData, ShadowBillResult, ActualBillResult, BvReportData, CalcExclusion, ShadowBillSection } from '../types/billTypes';
import { auditService } from './auditService';
import { STEP_LABELS, type WorkflowStep } from '../lib/billWorkflow';
import { validateBillPeriod } from '../utils/billPeriod';
import type { InvoiceEmailLike } from '../utils/billParseStatus';
import type { BulkRefreshResult } from '../utils/billRefreshSummary';
import type { ArchiveScope } from '../utils/archiveScope';
import type { ArchiveReason } from '../utils/archiveReasons';
import { ARCHIVE_REASON_LABELS } from '../utils/archiveReasons';
import { periodTakenBy, periodTakenMessage, type PeriodLike } from '../lib/periodOverlap';
import { effectiveInvoiceResult } from '../lib/effectiveInvoice';
import { calculateShadowBill } from './shadowCalculationService';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  return data?.organisation_id ?? null;
}

function calcDays(from: string, to: string): number {
  const ms = new Date(to).getTime() - new Date(from).getTime();
  return Math.round(ms / 86_400_000) + 1;
}

// Job 81. Applied to a Supabase query builder rather than to the returned rows,
// so an archived bill never crosses the wire for the common case.
//
// The default is 'active' everywhere and that direction is load-bearing: a call
// site that forgets the argument HIDES archived rows. The opposite default would
// COUNT them, which is a silently wrong number in a client-facing report.
function withArchiveScope<T>(query: T, scope: ArchiveScope): T {
  const q = query as unknown as {
    is: (col: string, val: null) => unknown;
    not: (col: string, op: string, val: null) => unknown;
  };
  if (scope === 'active') return q.is('archived_at', null) as unknown as T;
  if (scope === 'archived') return q.not('archived_at', 'is', null) as unknown as T;
  return query;
}

export const billService = {
  listBySite: async (siteId: string, scope: ArchiveScope = 'active'): Promise<ApiResponse<BvBill[]>> => {
    try {
      const { data, error } = await withArchiveScope(
        supabase
          .from('bv_bill')
          .select('*')
          .eq('site_id', siteId),
        scope,
      ).order('bill_from', { ascending: false });
      if (error) throw error;
      return { success: true, data: (data ?? []) as BvBill[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Set (or clear, with null) the person-set status step on bills — Follow up
   * with Retailer, Retailer Corrected Mismatch, Waiting on Customer Response,
   * Completed. Each change is recorded on the bill's history.
   */
  setWorkflowStep: async (
    billIds: string[],
    step: WorkflowStep | null,
  ): Promise<ApiResponse<{ updated: string[] }>> => {
    try {
      if (!billIds.length) return { success: false, error: 'No bills selected' };
      const { data: before, error: readErr } = await supabase
        .from('bv_bill')
        .select('id, site_id, workflow_status')
        .in('id', billIds);
      if (readErr) throw readErr;

      const { data, error } = await supabase
        .from('bv_bill')
        .update({ workflow_status: step, updated_at: new Date().toISOString() })
        .in('id', billIds)
        .select('id');
      if (error) throw error;
      const updated = ((data ?? []) as { id: string }[]).map((r) => r.id);

      // Audit after the write, and only for bills whose step actually changed.
      const prior = new Map(((before ?? []) as { id: string; site_id: string; workflow_status: WorkflowStep | null }[])
        .map((r) => [r.id, r]));
      await Promise.all(updated
        .map((id) => prior.get(id))
        .filter((r): r is { id: string; site_id: string; workflow_status: WorkflowStep | null } => !!r && r.workflow_status !== step)
        .map((r) => auditService.log({
          entity_type: 'bill',
          entity_id: r.id,
          site_id: r.site_id,
          action: 'status_change',
          field: 'workflow_status',
          old_value: r.workflow_status,
          new_value: step,
          note: step ? `Status set to ${STEP_LABELS[step]}` : 'Status step cleared',
        })));

      if (updated.length < billIds.length) {
        return { success: false, error: `${billIds.length - updated.length} of ${billIds.length} could not be updated`, data: { updated } };
      }
      return { success: true, data: { updated } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  listAll: async (scope: ArchiveScope = 'active'): Promise<ApiResponse<BvBill[]>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      // An unbounded select is capped at 1000 rows by Supabase with no error.
      // The Bills list filters and totals this array client-side, so a silent
      // truncation reads as "these are all the bills" — page through instead.
      // Ordered by created_at then id so paging is stable across requests.
      const all: BvBill[] = [];
      const PAGE_SIZE = 1000;
      let page = 0;
      for (;;) {
        const { data, error } = await withArchiveScope(
          supabase
            .from('bv_bill')
            .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, current_retailer, drive_link, client:client_id(name, drive_link))')
            .eq('org_id', orgId),
          scope,
        )
          .order('created_at', { ascending: false })
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as BvBill[]));
        if (!data?.length || data.length < PAGE_SIZE) break;
        page++;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Every invoice email that is attached to a bill, with just enough of its
   * attachments to derive a parse state (see utils/billParseStatus).
   *
   * The Bills list filters on parse status, and that fact lives one table away
   * from bv_bill. Paged for the same reason `listAll` is: an unbounded select
   * stops at 1000 rows with no error, which here would read as "these bills
   * never had an invoice emailed" rather than as a truncation.
   */
  listInvoiceEmails: async (): Promise<ApiResponse<InvoiceEmailLike[]>> => {
    try {
      const all: InvoiceEmailLike[] = [];
      const PAGE_SIZE = 1000;
      let page = 0;
      for (;;) {
        const { data, error } = await supabase
          .from('bv_invoice_email')
          .select('id, bill_id, assigned_at, attachments:bv_invoice_email_attachment(parsing_status, parse_error, updated_at)')
          .not('bill_id', 'is', null)
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all.push(...((data ?? []) as unknown as InvoiceEmailLike[]));
        if (!data?.length || data.length < PAGE_SIZE) break;
        page++;
      }
      return { success: true, data: all };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  getById: async (id: string): Promise<ApiResponse<BvBill>> => {
    try {
      const { data, error } = await supabase
        .from('bv_bill')
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, current_retailer, drive_link, client:client_id(name, drive_link))')
        .eq('id', id)
        .single();
      if (error) throw error;
      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (form: BvBillFormData): Promise<ApiResponse<BvBill>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      // One period, one active bill. A second bill for a period the site
      // already has is a twin that double-counts the invoice; the hand-made
      // duplicates on dev all started here. Point at the existing bill instead.
      const existing = await supabase
        .from('bv_bill')
        .select('id, bill_from, bill_to')
        .eq('site_id', form.site_id)
        .is('archived_at', null)
        .lte('bill_from', form.bill_to)
        .gte('bill_to', form.bill_from);
      if (existing.error) throw existing.error;
      const taken = periodTakenBy((existing.data ?? []) as PeriodLike[], '', form.bill_from, form.bill_to);
      if (taken) return { success: false, error: periodTakenMessage(taken) };

      const billing_days = calcDays(form.bill_from, form.bill_to);
      const peak = form.peak_kwh !== '' ? Number(form.peak_kwh) : 0;
      const shoulder = form.shoulder_kwh !== '' ? Number(form.shoulder_kwh) : 0;
      const off_peak = form.off_peak_kwh !== '' ? Number(form.off_peak_kwh) : 0;

      const { data, error } = await supabase
        .from('bv_bill')
        .insert({
          org_id: orgId,
          site_id: form.site_id,
          bill_from: form.bill_from,
          bill_to: form.bill_to,
          billing_days,
          peak_kwh: peak || null,
          shoulder_kwh: shoulder || null,
          off_peak_kwh: off_peak || null,
          total_kwh: peak + shoulder + off_peak || null,
          feed_in_kwh: form.feed_in_kwh !== '' ? Number(form.feed_in_kwh) : null,
          peak_demand_kva: form.peak_demand_kva !== '' ? Number(form.peak_demand_kva) : null,
          notes: form.notes || null,
        })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * The active bill whose period overlaps from–to, creating it only when there
   * is none. One locked database call (migration 124): a find followed by a
   * separate insert let two callers each create the same period.
   */
  findOrCreatePeriod: async (
    siteId: string,
    from: string,
    to: string,
  ): Promise<ApiResponse<{ bill: BvBill; created: boolean }>> => {
    try {
      const { data, error } = await supabase.rpc('bv_find_or_create_bill_period', {
        p_site_id: siteId,
        p_from: from,
        p_to: to,
      });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as { bill_id: string; created: boolean } | undefined;
      if (!row?.bill_id) throw new Error('Failed to create bill period');
      const bill = await billService.getById(row.bill_id);
      if (!bill.success || !bill.data) throw new Error(bill.error ?? 'Failed to load bill period');
      return { success: true, data: { bill: bill.data, created: row.created } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  updateUsage: async (billId: string, form: BvBillFormData): Promise<ApiResponse<BvBill>> => {
    try {
      // Read the current figures first so the audit trail can show what the
      // hand-entered values replaced (migration 066).
      const { data: before } = await supabase
        .from('bv_bill')
        .select('site_id, bill_from, bill_to, peak_kwh, shoulder_kwh, off_peak_kwh, total_kwh, feed_in_kwh, peak_demand_kva, notes')
        .eq('id', billId)
        .single();

      const billing_days = calcDays(form.bill_from, form.bill_to);
      const peak = form.peak_kwh !== '' ? Number(form.peak_kwh) : 0;
      const shoulder = form.shoulder_kwh !== '' ? Number(form.shoulder_kwh) : 0;
      const off_peak = form.off_peak_kwh !== '' ? Number(form.off_peak_kwh) : 0;

      const { data, error } = await supabase
        .from('bv_bill')
        .update({
          bill_from: form.bill_from,
          bill_to: form.bill_to,
          billing_days,
          peak_kwh: peak || null,
          shoulder_kwh: shoulder || null,
          off_peak_kwh: off_peak || null,
          total_kwh: peak + shoulder + off_peak || null,
          feed_in_kwh: form.feed_in_kwh !== '' ? Number(form.feed_in_kwh) : null,
          peak_demand_kva: form.peak_demand_kva !== '' ? Number(form.peak_demand_kva) : null,
          notes: form.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', billId)
        .select()
        .single();
      if (error) throw error;

      if (before) {
        const after = data as unknown as Record<string, unknown>;
        void auditService.logFieldChanges(
          { entity_type: 'bill', entity_id: billId, site_id: (before as { site_id?: string }).site_id ?? null },
          before as Record<string, unknown>,
          {
            bill_from: after.bill_from, bill_to: after.bill_to,
            peak_kwh: after.peak_kwh, shoulder_kwh: after.shoulder_kwh,
            off_peak_kwh: after.off_peak_kwh, total_kwh: after.total_kwh,
            feed_in_kwh: after.feed_in_kwh, peak_demand_kva: after.peak_demand_kva,
            notes: after.notes,
          },
        );
      }

      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Change only the bill period (job 48).
   *
   *  updateUsage also writes the period, but it takes the whole usage form — so
   *  calling it from the Invoices tab, which has no usage fields, would blank the
   *  kWh figures that were entered or parsed. This touches the period and the
   *  billing days derived from it, and nothing else: usage, parsed invoice values
   *  and the expected invoice are all left alone. */
  updatePeriod: async (
    billId: string,
    billFrom: string,
    billTo: string,
  ): Promise<ApiResponse<BvBill>> => {
    try {
      const invalid = validateBillPeriod(billFrom, billTo);
      if (invalid) return { success: false, error: invalid };

      const { data: before, error: beforeErr } = await supabase
        .from('bv_bill')
        .select('site_id, bill_from, bill_to')
        .eq('id', billId)
        .single();
      if (beforeErr) throw beforeErr;

      // Two bills covering the same period on one site is the kind of duplicate
      // that only shows up later as a confusing pair of bill runs.
      const siteId = (before as { site_id?: string } | null)?.site_id;
      if (siteId) {
        const { data: clash, error: clashErr } = await supabase
          .from('bv_bill')
          .select('id')
          .eq('site_id', siteId)
          .eq('bill_from', billFrom)
          .eq('bill_to', billTo)
          .neq('id', billId)
          .limit(1);
        if (clashErr) throw clashErr;
        if (clash?.length) {
          return {
            success: false,
            error: 'Another bill on this site already covers that period. '
              + 'Edit that bill instead, or choose a different period.',
          };
        }
      }

      const { data, error } = await supabase
        .from('bv_bill')
        .update({
          bill_from: billFrom,
          bill_to: billTo,
          billing_days: calcDays(billFrom, billTo),
          updated_at: new Date().toISOString(),
        })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;

      if (before) {
        const after = data as unknown as Record<string, unknown>;
        void auditService.logFieldChanges(
          { entity_type: 'bill', entity_id: billId, site_id: siteId ?? null },
          before as Record<string, unknown>,
          { bill_from: after.bill_from, bill_to: after.bill_to },
        );
      }

      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Single-bill Recalculate. Prices the usage the bill already holds — never
   * re-derived from the invoice, so hand-entered and NEM usage stand — but keeps
   * two rules of the server refresh (billRefreshService): a sent report is left
   * as sent, and the headline actual follows the invoice the report prints.
   * Pass the bill with its site joined; pricing reads the site.
   */
  recalculate: async (bill: BvBill): Promise<ApiResponse<BvBill>> => {
    try {
      const { data: sent, error: sentError } = await supabase.rpc('bv_bill_report_sent', { p_bill_id: bill.id });
      // Unknown counts as sent, as on the server: a customer's figures moved by
      // accident cannot be taken back.
      if (sentError) return { success: false, error: `Could not check whether this report was sent: ${sentError.message}` };
      if (sent !== false) {
        return { success: false, error: 'This report has already been sent to the customer, so its figures were left as sent.' };
      }

      const invoiceTotal = effectiveInvoiceResult(bill)?.total;
      let priced = bill;
      if (typeof invoiceTotal === 'number' && invoiceTotal !== bill.actual_total) {
        const { error } = await supabase
          .from('bv_bill')
          .update({ actual_total: invoiceTotal, updated_at: new Date().toISOString() })
          .eq('id', bill.id);
        if (error) throw error;
        priced = { ...bill, actual_total: invoiceTotal };
      }

      const result = await calculateShadowBill(supabase, priced);
      return billService.saveShadowResult(bill.id, result);
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  saveShadowResult: async (billId: string, result: ShadowBillResult): Promise<ApiResponse<BvBill>> => {
    try {
      const { data: before } = await supabase
        .from('bv_bill')
        .select('site_id, shadow_result')
        .eq('id', billId)
        .single();

      const { data, error } = await supabase
        .from('bv_bill')
        .update({ shadow_result: result, updated_at: new Date().toISOString() })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;

      // A shadow bill is the number the whole validation rests on — record every
      // re-run, with the total it moved from and to.
      const prevTotal = (before?.shadow_result as ShadowBillResult | null)?.total ?? null;
      void auditService.log({
        entity_type: 'bill',
        entity_id:   billId,
        site_id:     (before as { site_id?: string } | null)?.site_id ?? null,
        action:      'recalculation',
        field:       'shadow_total',
        old_value:   prevTotal,
        new_value:   result.total,
      });

      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Save hand-corrected Expected Invoice values (job 52).
   *
   *  Separate from saveShadowResult because the two mean different things: that
   *  one records a calculation, this one records a person overruling it. The
   *  `overridden_at` stamp is what stops the four automatic recalculation paths
   *  from replacing the correction without saying so. */
  saveShadowOverride: async (
    billId: string,
    result: ShadowBillResult,
    note?: string | null,
  ): Promise<ApiResponse<BvBill>> => {
    try {
      const { data: before } = await supabase
        .from('bv_bill')
        .select('site_id, shadow_result')
        .eq('id', billId)
        .single();

      const overridden: ShadowBillResult = {
        ...result,
        overridden_at: new Date().toISOString(),
        override_note: note?.trim() || null,
      };

      const { data, error } = await supabase
        .from('bv_bill')
        .update({ shadow_result: overridden, updated_at: new Date().toISOString() })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;

      // The expected invoice is the number a dispute rests on, so an override of
      // it is exactly the change someone will later be asked to justify.
      const prevTotal = (before?.shadow_result as ShadowBillResult | null)?.total ?? null;
      void auditService.log({
        entity_type: 'bill',
        entity_id:   billId,
        site_id:     (before as { site_id?: string } | null)?.site_id ?? null,
        action:      'manual_override',
        field:       'expected_total',
        old_value:   prevTotal,
        new_value:   overridden.total,
        note:        note?.trim()
          ? `Expected invoice edited manually — ${note.trim()}`
          : 'Expected invoice edited manually',
      });

      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Persist the charges excluded from the expected invoice (job 53).
   *
   *  Follows the saveShadowOverride pattern: it writes the list and records the
   *  change through auditService, so an omission from a total can always be
   *  explained later. Exclusions live on bv_bill.calc_exclusions — not in
   *  shadow_result — precisely so a recalculation cannot wipe them. */
  setCalcExclusions: async (
    billId: string,
    exclusions: CalcExclusion[],
  ): Promise<ApiResponse<BvBill>> => {
    try {
      const { data: before } = await supabase
        .from('bv_bill')
        .select('site_id, calc_exclusions')
        .eq('id', billId)
        .single();

      const { data, error } = await supabase
        .from('bv_bill')
        .update({ calc_exclusions: exclusions, updated_at: new Date().toISOString() })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;

      const prev = Array.isArray((before as { calc_exclusions?: unknown } | null)?.calc_exclusions)
        ? ((before as { calc_exclusions: CalcExclusion[] }).calc_exclusions).length
        : 0;
      void auditService.log({
        entity_type: 'bill',
        entity_id:   billId,
        site_id:     (before as { site_id?: string } | null)?.site_id ?? null,
        action:      'manual_override',
        field:       'calc_exclusions',
        old_value:   prev,
        new_value:   exclusions.length,
        note:        exclusions.length
          ? `Expected invoice charge exclusions updated — ${exclusions.length} excluded`
          : 'Expected invoice charge exclusions cleared',
      });

      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  updateActualTotal: async (billId: string, actualTotal: number): Promise<ApiResponse<BvBill>> => {
    try {
      const { data: before } = await supabase
        .from('bv_bill')
        .select('site_id, actual_total')
        .eq('id', billId)
        .single();

      const { data, error } = await supabase
        .from('bv_bill')
        .update({
          actual_total: actualTotal,
          actual_entered_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;

      // Typing the retailer's total by hand is the classic manual override —
      // exactly the change a client dispute later asks us to justify.
      void auditService.log({
        entity_type: 'bill',
        entity_id:   billId,
        site_id:     (before as { site_id?: string } | null)?.site_id ?? null,
        action:      'manual_override',
        field:       'actual_total',
        old_value:   before?.actual_total ?? null,
        new_value:   actualTotal,
        note:        'Actual invoice total entered manually',
      });

      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  saveInvoicePath: async (billId: string, invoicePath: string): Promise<ApiResponse<BvBill>> => {
    try {
      const { data, error } = await supabase
        .from('bv_bill')
        .update({ invoice_path: invoicePath, updated_at: new Date().toISOString() })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;
      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // Deleting a wrongly-uploaded invoice must not take the bill period with it —
  // the usage figures and shadow result on that row are entered separately and
  // still valid. This detaches the PDF and the values parsed out of it, leaving
  // the period ready for the right invoice.
  removeInvoice: async (billId: string): Promise<ApiResponse<BvBill>> => {
    try {
      const { data: before } = await supabase
        .from('bv_bill')
        .select('site_id, invoice_path')
        .eq('id', billId)
        .single();

      const { data, error } = await supabase
        .from('bv_bill')
        .update({
          invoice_path: null,
          actual_parsed_result: null,
          actual_total: null,
          actual_entered_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;

      // Storage last: an orphaned file is recoverable, a row pointing at a
      // deleted PDF is not.
      const oldPath = (before as { invoice_path?: string | null } | null)?.invoice_path;
      if (oldPath) {
        await supabase.storage.from('bv-attachments').remove([oldPath]);
      }

      void auditService.log({
        entity_type: 'bill',
        entity_id:   billId,
        site_id:     (before as { site_id?: string } | null)?.site_id ?? null,
        action:      'deleted',
        field:       'invoice_path',
        old_value:   oldPath ?? null,
        new_value:   null,
        note:        'Uploaded invoice PDF deleted — bill period kept',
      });

      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Saves a hand-corrected invoice.
   *
   * Writes `actual_edited_result`, NEVER `actual_parsed_result`. This used to
   * overwrite the parse, so one correction destroyed the original extraction —
   * unrecoverable, because re-parsing costs an LLM call and is not
   * deterministic. The parse now stays exactly as the parser left it and the
   * correction sits beside it; `effectiveInvoice()` decides which is used.
   *
   * `actual_total` follows the EFFECTIVE figures, since the reports list and the
   * site summary read that column rather than the JSON.
   */
  /**
   * Applies a hand-corrected invoice, or undoes one.
   *
   * Goes through the server rather than writing the row directly, because saving
   * the corrected JSON is only part of the job. `bv_bill.peak_kwh` and its
   * siblings are what the expected invoice is priced against, and they are
   * written at parse time — correcting "Peak 1682.685 kWh" to 1700 used to change
   * the invoice side of the comparison and leave the expected side pricing the
   * old figure, so the variance shown to a client was wrong with nothing on
   * screen to say so. The route re-derives usage and re-prices in one place.
   *
   * `actual_parsed_result` is never touched by either path.
   */
  applyEditedInvoice: async (
    billId: string,
    payload: { result: ActualBillResult; note?: string } | { revert: true },
  ): Promise<ApiResponse<{ bill: BvBill; usageUpdated: string[]; usageUnchanged: string[]; repriced: boolean }>> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/bill-validation/bills/${billId}/edited-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? ''}`,
          'x-api-key': 'scopingai',
        },
        body: JSON.stringify({ ...payload, editedBy: session?.user?.id ?? null }),
      });
      const json = await res.json() as { success: boolean; data?: never; error?: string };
      if (!res.ok || !json.success) return { success: false, error: json.error ?? 'Save failed' };
      return { success: true, data: json.data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Saves a hand-corrected invoice. See applyEditedInvoice. */
  saveActualResult: async (billId: string, result: ActualBillResult, note?: string): Promise<ApiResponse<BvBill>> => {
    const res = await billService.applyEditedInvoice(billId, { result, note });
    return res.success && res.data
      ? { success: true, data: res.data.bill }
      : { success: false, error: res.error };
  },

  /** Drops every correction and returns to what the parser read. */
  revertActualResult: async (billId: string): Promise<ApiResponse<BvBill>> => {
    const res = await billService.applyEditedInvoice(billId, { revert: true });
    return res.success && res.data
      ? { success: true, data: res.data.bill }
      : { success: false, error: res.error };
  },


  saveReportData: async (billId: string, reportData: BvReportData): Promise<ApiResponse<BvBill>> => {
    try {
      const { data, error } = await supabase
        .from('bv_bill')
        .update({ report_data: reportData, updated_at: new Date().toISOString() })
        .eq('id', billId)
        .select('*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, drive_link, client:client_id(name, drive_link))')
        .single();
      if (error) throw error;
      return { success: true, data: data as BvBill };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * `options.sections` narrows the re-parse: only those sections are replaced,
   * the rest of the stored result is kept. The extraction is an LLM call and is
   * not deterministic, so re-running the whole document to correct one section
   * can just as easily corrupt the sections that were already right.
   */
  parseInvoice: async (
    billId: string,
    storagePath: string,
    options?: { sections?: ShadowBillSection[]; extraInstructions?: string; retailerId?: string | null },
  ): Promise<ApiResponse<ActualBillResult>> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? '';
      const res = await fetch(`${API_BASE}/api/bill-validation/bills/${billId}/parse-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-api-key': 'scopingai',
        },
        body: JSON.stringify({
          storage_path: storagePath,
          ...(options?.sections?.length ? { sections: options.sections } : {}),
          ...(options?.extraInstructions?.trim() ? { extra_instructions: options.extraInstructions.trim() } : {}),
          // undefined = leave whatever retailer is already on the bill alone.
          // null = explicitly clear it back to the standard prompt.
          ...(options?.retailerId !== undefined ? { retailer_id: options.retailerId } : {}),
        }),
      });
      const json = await res.json() as { success: boolean; data?: ActualBillResult; error?: string };
      if (!json.success) return { success: false, error: json.error ?? 'Parse failed' };
      return { success: true, data: json.data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Rebuild everything a validation report derives, for a set of bills.
   *
   * Usage → actual_total → expected invoice, per bill, server-side (the usage
   * step reads invoiceUsageService, which only exists there). `replaceOverridden`
   * carries the answer to the confirm prompt: without it, a hand-corrected
   * expected invoice is left exactly as it is and reported back as skipped.
   */
  refreshBills: async (
    billIds: string[],
    options: { replaceOverridden?: boolean } = {},
  ): Promise<ApiResponse<BulkRefreshResult>> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/bill-validation/bills/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'scopingai',
          ...(session?.user?.id ? { 'x-user-id': session.user.id } : {}),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ billIds, replaceOverridden: !!options.replaceOverridden }),
      });
      const json = await res.json() as { success: boolean; data?: BulkRefreshResult; error?: string };
      if (!json.success) return { success: false, error: json.error ?? 'Refresh failed' };
      return { success: true, data: json.data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Job 81 — retire invoices without destroying them. One update for the batch,
   * then one audit event per bill.
   *
   * A partial write is reported as a failure, not as success. Supabase returns
   * zero rows rather than an error when RLS rejects a row, so a short result
   * means "some of these were not yours" and the caller has to see that.
   */
  archive: async (
    billIds: string[],
    reason: ArchiveReason,
    note?: string,
  ): Promise<ApiResponse<{ archived: string[]; failedIds: string[] }>> => {
    try {
      if (!billIds.length) return { success: false, error: 'No invoices selected' };

      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('bv_bill')
        .update({
          archived_at: new Date().toISOString(),
          archive_reason: reason,
          archive_note: note?.trim() || null,
          archived_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .in('id', billIds)
        .select('id, site_id');
      if (error) throw error;

      const rows = (data ?? []) as { id: string; site_id: string }[];
      const archived = rows.map((r) => r.id);
      const failedIds = billIds.filter((id) => !archived.includes(id));

      // Audit after the write: an event describing a change that did not happen
      // is worse than a missing one.
      await Promise.all(rows.map((r) => auditService.log({
        entity_type: 'bill',
        entity_id: r.id,
        site_id: r.site_id,
        action: 'archived',
        field: 'archived_at',
        old_value: null,
        new_value: reason,
        note: note?.trim()
          ? `${ARCHIVE_REASON_LABELS[reason]} — ${note.trim()}`
          : ARCHIVE_REASON_LABELS[reason],
      })));

      if (failedIds.length) {
        return {
          success: false,
          error: `${failedIds.length} of ${billIds.length} could not be archived`,
          data: { archived, failedIds },
        };
      }
      return { success: true, data: { archived, failedIds: [] } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /**
   * Job 81 — the inverse. Idempotent: restoring an already-active bill is a
   * no-op, not a failure.
   *
   * The Bills list offers "Restore selected" unconditionally, so a mixed
   * selection (some archived, some already active) is a real input, not an
   * edge case. Filtering happens here, in the service, by reading which ids
   * are actually archived before acting — not by adding `archived_at IS NOT
   * NULL` to the UPDATE's predicate. That would make Supabase report every
   * already-active id as a row the update didn't touch, which is exactly the
   * "could not be restored" failure path below and would put a false error in
   * front of the user for bills that were never archived in the first place.
   */
  restore: async (billIds: string[]): Promise<ApiResponse<{ restored: string[]; failedIds: string[] }>> => {
    try {
      if (!billIds.length) return { success: false, error: 'No invoices selected' };

      const { data: current, error: readError } = await supabase
        .from('bv_bill')
        .select('id, archived_at')
        .in('id', billIds);
      if (readError) throw readError;

      const currentRows = (current ?? []) as { id: string; archived_at: string | null }[];
      const foundIds = new Set(currentRows.map((r) => r.id));
      // Already active: nothing to change, nothing to audit — a no-op counts
      // as restored, not as a failure.
      const alreadyActive = currentRows.filter((r) => !r.archived_at).map((r) => r.id);
      const toRestore = currentRows.filter((r) => !!r.archived_at).map((r) => r.id);
      // Not returned by the read at all: missing, or RLS silently hid it —
      // either way this call cannot restore it, so it is a real failure.
      const unresolved = billIds.filter((id) => !foundIds.has(id));

      let updatedIds: string[] = [];
      if (toRestore.length) {
        const { data, error } = await supabase
          .from('bv_bill')
          .update({
            archived_at: null,
            archive_reason: null,
            archive_note: null,
            archived_by: null,
            updated_at: new Date().toISOString(),
          })
          .in('id', toRestore)
          .select('id, site_id');
        if (error) throw error;

        const rows = (data ?? []) as { id: string; site_id: string }[];
        updatedIds = rows.map((r) => r.id);

        await Promise.all(rows.map((r) => auditService.log({
          entity_type: 'bill',
          entity_id: r.id,
          site_id: r.site_id,
          action: 'restored',
          field: 'archived_at',
          old_value: 'archived',
          new_value: null,
        })));
      }

      const restored = [...updatedIds, ...alreadyActive];
      const failedIds = [...unresolved, ...toRestore.filter((id) => !updatedIds.includes(id))];

      if (failedIds.length) {
        return {
          success: false,
          error: `${failedIds.length} of ${billIds.length} could not be restored`,
          data: { restored, failedIds },
        };
      }
      return { success: true, data: { restored, failedIds: [] } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  delete: async (id: string): Promise<ApiResponse<null>> => {
    try {
      // Fetch invoice_path before deleting so we can clean up storage
      const { data: billData } = await supabase
        .from('bv_bill')
        .select('invoice_path')
        .eq('id', id)
        .maybeSingle();

      const { error } = await supabase.from('bv_bill').delete().eq('id', id);
      if (error) throw error;

      if (billData?.invoice_path) {
        await supabase.storage.from('bv-attachments').remove([billData.invoice_path]);
      }
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
